<template>
  <div class="root">
    <div class="header">
      <span>{{ $t("publishSharedLink.title") }}</span>
      <span class="close" @click="closePublishSharedLink">{{ $t("publishSharedLink.closeWindow") }}</span>
    </div>
    <div class="body">
      <div class="shared-link-list">
        <div class="list">
          <div v-if="sharedLinkList.length === 0" class="description">
            <span>{{ $t("publishSharedLink.noLinks") }}</span>
          </div>
          <transition-group name="tran" v-if="sharedLinkList.length">
            <div
              class="list-item"
              v-for="item in sharedLinkList"
              :key="item.sharedLinkId"
              :class="{ active: selected && selected.sharedLinkId === item.sharedLinkId }"
              @click="select(item)"
            >
              <div class="name ellipsis">{{ item.name || "NO TITLE" }}</div>
              <div class="link-wrapper">
                <div class="link ellipsis">
                  <a v-if="item.enabled" :href="resolveSharedLinkPath(item)" target="_blank" rel="noopener noreferrer">
                    {{ sharedLinkPath(item) }}
                  </a>
                  <a v-else class="disabled-link">{{ $t("publishSharedLink.disabledLink") }}</a>
                </div>
                <div
                  v-if="item.enabled"
                  class="copy"
                  v-clipboard:copy="sharedLinkPath(item)"
                  v-clipboard:success="onCopy"
                >
                  <img src="@/assets/img/icon/content_copy.svg" alt="content-copy" />
                </div>
              </div>
            </div>
          </transition-group>
        </div>
        <div class="create-shared-link" @click="createSharedLink">
          <span>{{ $t("publishSharedLink.createNew") }}</span>
          <span v-if="!plan" class="note">{{ $t("publishSharedLink.freePlanNote") }}</span>
        </div>
      </div>
      <div class="container" :key="selected ? selected.sharedLinkId : null">
        <div class="main">
          <div class="shared-link-name" v-if="selected">
            <div class="header-container">
              <div class="header-title">{{ $t("publishSharedLink.linkNameHeader") }}</div>
            </div>
            <div class="input-wrapper">
              <input-text
                class="input"
                :label="$t('publishSharedLink.linkNamePlaceholder')"
                type="text"
                :maxlength="50"
                v-model="selected.name"
              />
            </div>
          </div>
          <div class="manuscript-list" v-if="selected">
            <div class="header-container">
              <div class="header-title">{{ $t("publishSharedLink.manuscriptSelectionHeader") }}</div>
              <div class="dropup-menu" @click="toggleMenu(SharedLinkKind.MANUSCRIPT)">
                <img src="@/assets/img/icon/expand_less.svg" :class="{ open: isOpenManuscriptList }" />
              </div>
            </div>
            <div class="list" v-if="isOpenManuscriptList">
              <div
                class="list-item"
                v-for="manuscript in manuscriptList"
                :key="manuscript.key"
                @click="onClickItem(manuscript, SharedLinkKind.MANUSCRIPT)"
              >
                <checkbox-icon :isChecked="isChecked(manuscript, SharedLinkKind.MANUSCRIPT)" />
                <div class="manuscript-title ellipsis">{{ manuscript.title || $t("publishSharedLink.untitledItem") }}</div>
              </div>
            </div>
            <div class="header-container">
              <div class="header-title">{{ $t("publishSharedLink.plotSelectionHeader") }}</div>
              <div class="dropup-menu" @click="toggleMenu(SharedLinkKind.PLOT)">
                <img src="@/assets/img/icon/expand_less.svg" :class="{ open: isOpenPlotList }" />
              </div>
            </div>
            <div class="list" v-if="isOpenPlotList">
              <div
                class="list-item"
                v-for="plot in plotList"
                :key="plot.plotId"
                @click="onClickItem(plot, SharedLinkKind.PLOT)"
              >
                <checkbox-icon :isChecked="isChecked(plot, SharedLinkKind.PLOT)" />
                <div class="manuscript-title ellipsis">{{ plot.name || $t("publishSharedLink.untitledItem") }}</div>
              </div>
            </div>
          </div>
          <div class="warning" v-if="selected">
            <warning-icon class="warning-icon" />
            <div class="warning-title">{{ $t("publishSharedLink.warningTitle") }}</div>
            <div class="warning-text">{{ $t("publishSharedLink.warningText") }}</div>
          </div>
          <div v-else class="description"><span>{{ $t("publishSharedLink.selectFromList") }}</span></div>
        </div>
        <div class="controller">
          <div class="confirm-action-buttons">
            <button v-if="plan !== 0" class="delete" :disabled="!selected" @click="onClickDelete">
              <delete-icon class="delete-icon" />
            </button>
            <div v-if="selected">
              <button v-if="selected.enabled" class="button change-status" @click="onClickStop">{{ $t("publishSharedLink.stopSharing") }}</button>
              <button v-else class="button change-status" @click="resumeSharedLink">{{ $t("publishSharedLink.resumeSharing") }}</button>
            </div>
          </div>
          <div class="wrapper">
            <button
              class="button copy"
              :disabled="!selected || !selected.enabled"
              v-clipboard:copy="sharedLinkPath(selected)"
              v-clipboard:success="onCopy"
            >
              {{ $t("publishSharedLink.copyLink") }}
            </button>
            <button class="button save" :disabled="!selected" @click="save">{{ $t("publishSharedLink.save") }}</button>
          </div>
        </div>
        <div v-if="isDeleting || isStopping" class="mask">
          <h1>{{ isDeleting ? $t("publishSharedLink.deleteConfirmTitle") : $t("publishSharedLink.stopConfirmTitle") }}</h1>
          <div>
            <div>
              {{
                isDeleting
                  ? $t("publishSharedLink.deleteConfirmBody1")
                  : $t("publishSharedLink.stopConfirmBody1")
              }}
            </div>
            <div>
              {{
                isDeleting
                  ? $t("publishSharedLink.deleteConfirmBody2")
                  : $t("publishSharedLink.stopConfirmBody2")
              }}
            </div>
          </div>
          <div v-if="isDeleting" class="mask-controller">
            <button class="button danger" @click="deleteSharedLink">{{ $t("publishSharedLink.deleteButton") }}</button>
            <button class="button cancel" @click="cancelDelete">{{ $t("common.cancel") }}</button>
          </div>
          <div v-else class="mask-controller">
            <button class="button danger" @click="stopSharedLink">{{ $t("publishSharedLink.stopButton") }}</button>
            <button class="button cancel" @click="cancelStop">{{ $t("common.cancel") }}</button>
          </div>
        </div>
      </div>
      <span class="info-belt" :class="{ visible: copied }">{{ $t("publishSharedLink.copiedMessage") }}</span>
      <span class="info-belt" :class="{ visible: saved }">{{ $t("publishSharedLink.savedMessage") }}</span>
      <span class="info-belt" :class="{ visible: deleted }">{{ $t("publishSharedLink.deletedMessage") }}</span>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import VueClipboard from "vue-clipboard2";
import clone from "lodash/cloneDeep";
import InputText from "@/components/atoms/InputText.vue";
import DeleteIcon from "@/components/atoms/DeleteIcon.vue";
import CheckboxIcon from "@/components/atoms/CheckboxIcon.vue";
import WarningIcon from "@/components/atoms/WarningIcon.vue";
import { Manuscript, SharedLink, Plan, SharedLinkKind } from "@/lib/models";
import { compareAsc } from "date-fns";
// NOTE: 機能紹介GIFを修正するため一時的にコメントアウト
// import { Dialog } from "@/lib/utils";
// import PremiumFeatureDialog, { PremiumFeatureDialogProps } from "@/components/ui/PremiumFeatureDialog.vue";
// import { downloadPublicImage } from "@/lib/s3";
import { Plot, SubPlot, OverallPlot, PlotList } from "@/lib/models/plot";

Vue.use(VueClipboard);

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    InputText,
    DeleteIcon,
    CheckboxIcon,
    WarningIcon,
  },
  props: {
    novelId: {
      type: String as PropType<string>,
    },
    kind: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      selected: null,
      name: "",
      copied: false,
      saved: false,
      deleted: false,
      isDeleting: false,
      isStopping: false,
      SharedLinkKind,
      isOpenManuscriptList: true,
      isOpenPlotList: true,
    };
  },
  destroyed() {
    this.closePublishSharedLink();
  },
  methods: {
    select(item) {
      this.selected = clone(item);
    },
    onClickItem(item, kind) {
      switch (kind) {
        case SharedLinkKind.MANUSCRIPT: {
          const manuscriptKey = (item as Manuscript).key;
          if (this.selected.manuscriptKeys.includes(manuscriptKey)) {
            this.selected.manuscriptKeys = this.selected.manuscriptKeys.filter(
              (x: string | undefined) => x !== manuscriptKey
            );
          } else {
            this.selected.manuscriptKeys.push(manuscriptKey);
          }
          break;
        }
        case SharedLinkKind.PLOT: {
          const { plotId } = item as OverallPlot | SubPlot;

          // プロット共有機能追加前のデータにはplotIds属性が存在しない為、
          // this.selected.plotIdがFalsyの値の場合空の配列を代入する。
          if (!this.selected.plotIds) {
            this.selected.plotIds = [];
          }

          if (this.selected.plotIds.includes(plotId)) {
            this.selected.plotIds = this.selected.plotIds.filter((x: string | undefined) => x !== plotId);
          } else {
            this.selected.plotIds.push(plotId);
          }
          break;
        }
        default:
      }
    },
    toggleMenu(kind) {
      switch (kind) {
        case SharedLinkKind.MANUSCRIPT:
          this.isOpenManuscriptList = !this.isOpenManuscriptList;
          break;
        case SharedLinkKind.PLOT:
          this.isOpenPlotList = !this.isOpenPlotList;
          break;
        default:
          break;
      }
    },
    closePublishSharedLink() {
      this.$store.dispatch("sharedLinkModule/closePublishSharedLink");
    },
    async createSharedLink() {
      if (this.isFree && this.sharedLinkListBelongOwner.length >= 3) {
        // NOTE: 機能紹介GIFを修正するため一時的にコメントアウト
        // const premiumFeatureDialog = new Dialog(PremiumFeatureDialog);
        // const data: PremiumFeatureDialogProps = {
        //   title: "共有リンクの無制限作成はプレミアム機能です",
        //   text: "原稿の共有リンクは、アカウントごとに3つまで無料で作成が可能となっております。プレミアム会員になると、共有リンクが無制限で作成することができますので、多くの方に作品を共有する場合はぜひご利用いただけますと幸いです。",
        //   image: (await downloadPublicImage("shared/images/gif/manuscript_share.gif")) as string,
        // };
        // premiumFeatureDialog.show(data);

        this.$router.push({ name: "subscriptionAnnounce", query: { from: "createSharedLink" } });

        return;
      }

      const { novelId } = this;
      this.$store.dispatch("sharedLinkModule/createSharedLink", {
        novelId,
        name: "",
        manuscriptKeys: [],
        plotIds: [],
        callback: () => {
          this.selected = clone(this.createdSharedLink);
          this.$store.dispatch("sharedLinkModule/initializeCreatedSharedLink");
          this.$store.dispatch("sharedLinkModule/initializeSharedLinkListBelongOwner");
        },
      });
    },
    isChecked(item, kind) {
      switch (kind) {
        case SharedLinkKind.MANUSCRIPT:
          return this.selected.manuscriptKeys.includes((item as Manuscript).key);
        case SharedLinkKind.PLOT:
          // プロット共有機能追加前のデータにはplotIds属性が存在しない為、falseを返す
          if (!this.selected.plotIds) {
            return false;
          }
          return this.selected.plotIds.includes((item as OverallPlot | SubPlot).plotId);
        default:
          return false;
      }
    },
    onCopy() {
      this.copied = true;
      setTimeout(() => (this.copied = false), 2000);
    },
    save() {
      const { sharedLinkId, novelId, name, manuscriptKeys, plotIds, enabled } = this.selected as SharedLink;
      this.$store.dispatch("sharedLinkModule/updateSharedLink", {
        sharedLinkId,
        novelId,
        name,
        manuscriptKeys,
        plotIds: plotIds || [],
        enabled,
        // NOTE: 共有リンク閲覧時の本文取得（sharedManuscriptContent）はオーナーのprivate本文を
        // 直接読む方式になっており、閲覧可能ディレクトリへの複製は不要になったため呼び出しを削除した。
        callback: () => {
          this.saved = true;
          setTimeout(() => (this.saved = false), 2000);
        },
      });
    },
    onClickDelete() {
      this.isDeleting = true;
    },
    cancelDelete() {
      this.isDeleting = false;
    },
    onClickStop() {
      this.isStopping = true;
    },
    cancelStop() {
      this.isStopping = false;
    },
    deleteSharedLink() {
      const { sharedLinkId, novelId } = this.selected as SharedLink;
      this.$store.dispatch("sharedLinkModule/deleteSharedLink", {
        sharedLinkId,
        novelId,
        callback: () => {
          this.$store.dispatch("sharedLinkModule/initializeSharedLinkListBelongOwner");
          this.selected = null;
          this.isDeleting = false;
          this.deleted = true;
          setTimeout(() => (this.deleted = false), 2000);
        },
      });
    },
    stopSharedLink() {
      const { sharedLinkId, novelId, name, manuscriptKeys, plotIds } = this.selected as SharedLink;
      this.$store.dispatch("sharedLinkModule/updateSharedLink", {
        sharedLinkId,
        novelId,
        name,
        manuscriptKeys,
        plotIds: plotIds || [],
        enabled: false,
        callback: () => {
          this.selected.enabled = false;
          this.isStopping = false;
        },
      });
    },
    resumeSharedLink() {
      const { sharedLinkId, novelId, name, manuscriptKeys, plotIds } = this.selected as SharedLink;
      this.$store.dispatch("sharedLinkModule/updateSharedLink", {
        sharedLinkId,
        novelId,
        name,
        manuscriptKeys,
        plotIds: plotIds || [],
        enabled: true,
        callback: () => {
          this.selected.enabled = true;
        },
      });
    },
  },
  computed: {
    sharedLinkList() {
      const sharedLinkList = this.$store.getters["sharedLinkModule/sharedLinkList"] as SharedLink[];
      const enabledList = sharedLinkList
        .filter((sharedLink) => sharedLink.enabled)
        .sort((x, y) => compareAsc(x.createdAt!, y.createdAt!));
      const disabledList = sharedLinkList
        .filter((sharedLink) => !sharedLink.enabled)
        .sort((x, y) => compareAsc(x.createdAt!, y.createdAt!));
      return enabledList.concat(disabledList);
    },
    sharedLinkListBelongOwner() {
      return this.$store.getters["sharedLinkModule/sharedLinkListBelongOwner"] as SharedLink[];
    },
    createdSharedLink() {
      return this.$store.getters["sharedLinkModule/createdSharedLink"];
    },
    manuscriptList() {
      return this.$store.getters["manuscriptModule/manuscriptList"];
    },
    sharedLinkPath() {
      return (item) => {
        if (!item) return undefined;

        // eslint-disable-next-line no-restricted-globals
        const { protocol, host } = location;
        const { sharedLinkId, novelId } = item;

        return `${protocol}//${host}/share/${sharedLinkId}/${novelId}`;
      };
    },
    resolveSharedLinkPath() {
      return (item) => {
        const { sharedLinkId, novelId } = item;

        return `/share/${sharedLinkId}/${novelId}`;
      };
    },
    plan() {
      return this.$store.getters["billingModule/plan"] as Plan;
    },
    isFree() {
      return this.plan === Plan.free;
    },
    plotList() {
      const plots: Plot = this.$store.getters["plotModule/plots"];
      const { novelId } = this.selected as SharedLink;
      const overallPlot = {
        name: this.$t("publishSharedLink.overallPlot") as string,
        plotId: `overall-plot-${novelId}`,
        ...plots,
      };
      const subPlots = this.$store.getters["plotModule/subPlots"];

      return [overallPlot, ...subPlots];
    },
  },
});

interface Data {
  selected: SharedLink | null;
  name: string;
  copied: boolean;
  saved: boolean;
  deleted: boolean;
  isDeleting: boolean;
  isStopping: boolean;
  SharedLinkKind: typeof SharedLinkKind;
  isOpenManuscriptList: boolean;
  isOpenPlotList: boolean;
}

interface Props {
  novelId: string;
  kind: string;
}

interface Computed {
  sharedLinkList: SharedLink[];
  sharedLinkListBelongOwner: SharedLink[];
  createdSharedLink: SharedLink;
  manuscriptList: Manuscript[];
  sharedLinkPath: (item: SharedLink | null) => string | undefined;
  resolveSharedLinkPath: (item: SharedLink) => string;
  plan: Plan;
  isFree: boolean;
  plotList: PlotList;
}

interface Methods {
  select: (item: SharedLink) => void;
  onClickItem: (item: Manuscript | OverallPlot | SubPlot, kind: string) => void;
  closePublishSharedLink: () => void;
  createSharedLink: () => void;
  isChecked: (item: Manuscript | OverallPlot | SubPlot, kind: string) => boolean;
  onCopy: () => void;
  save: () => void;
  onClickDelete: () => void;
  cancelDelete: () => void;
  onClickStop: () => void;
  cancelStop: () => void;
  deleteSharedLink: () => void;
  stopSharedLink: () => void;
  resumeSharedLink: () => void;
  toggleMenu: (kind: string) => void;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";
.root {
  width: 80%;
  height: 80%;
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);

  .header {
    color: var(--color-text-on-fixed-bg);
    background-color: var(--color-bg-dark);
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 30px;

    span {
      margin: 5px 10px;

      &.close {
        cursor: pointer;
      }
    }
  }

  .body {
    position: relative;
    display: flex;
    height: calc(100% - 30px);

    .description {
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;

      span {
        color: var(--color-text-low);
      }
    }

    .shared-link-list {
      box-sizing: border-box;
      width: 250px;
      display: flex;
      flex-direction: column;
      border-right: 1px solid var(--color-border);

      .list {
        height: calc(100% - 50px);
        overflow: auto;
        flex-grow: 1;

        .list-item {
          box-sizing: border-box;
          border-bottom: solid 1px var(--color-border);
          width: 100%;
          padding: 15px 10px;
          cursor: pointer;
          position: relative;
          @include hover-overlay;

          &.active {
            background-color: var(--color-surface-subtle);

            &::after {
              display: none;
            }
          }

          .name {
            font-size: 1.15em;
            font-weight: var(--font-weight-bold);
          }

          .link-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: center;

            .link {
              a {
                color: var(--color-primary);

                &.disabled-link {
                  color: var(--color-warning);
                  pointer-events: none;
                  text-decoration: underline;
                  text-decoration-color: var(--color-warning);
                }
              }
            }

            .copy {
              display: flex;
              align-items: center;
              cursor: pointer;

              img {
                width: 1em;
                filter: brightness(0);

                [data-app-theme="dark"] & {
                  filter: brightness(0) invert(1);
                }
              }
            }
          }
        }
      }

      .create-shared-link {
        height: 50px;
        color: var(--color-text-on-fixed-bg);
        background-color: var(--color-primary);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        font-size: 1.2em;
        position: relative;
        @include hover-overlay;

        .note {
          font-size: 0.5em;
        }
      }
    }

    .container {
      width: calc(100% - 250px);
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      position: relative;

      .main {
        height: calc(100% - 50px);
        display: flex;
        flex-direction: column;

        .header-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background-color: var(--color-surface-subtle);
          padding: 5px 10px;

          .header-title {
            font-size: 1.15em;
            font-weight: var(--font-weight-bold);
          }

          .dropup-menu {
            cursor: pointer;

            img {
              margin-right: 5px;
              transition: all 0.3s;
              filter: brightness(0);

              [data-app-theme="dark"] & {
                filter: brightness(0) invert(1);
              }

              &.open {
                transform: rotate(-180deg);
              }
            }
          }
        }

        .shared-link-name {
          height: 86px;

          .input-wrapper {
            display: flex;
            align-items: center;

            .input {
              width: 90%;
              margin: 10px;
            }
          }
        }

        .manuscript-list {
          display: flex;
          flex-direction: column;
          height: calc(100% - 86px - 58px);

          .list {
            overflow: auto;
            height: 50%;

            .list-item {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 10px;
              border-bottom: solid 1px var(--color-border);
              cursor: pointer;
              position: relative;
              @include hover-overlay;

              .manuscript-title {
                flex-grow: 1;
                margin-left: 10px;
                font-size: 1.15em;
              }
            }
          }
        }

        .warning {
          display: flex;
          align-items: center;
          background-color: var(--color-warning-bg);
          box-sizing: border-box;
          padding: 10px;
          height: 58px;
          color: var(--color-warning-text);

          .warning-title {
            font-weight: var(--font-weight-bold);
            padding-left: 5px;
            width: 90px;
          }

          .warning-text {
            flex-grow: 1;
          }
        }
      }

      .controller {
        height: 50px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-color: var(--color-surface-subtle);

        .confirm-action-buttons {
          display: flex;
          align-items: center;
          margin: 0 10px;

          .delete {
            background-color: transparent;
            border: solid 2px var(--color-warning);
            border-radius: var(--radius-circle);
            display: flex;
            align-items: center;
            padding: 5px;
            margin-left: 10px;
            cursor: pointer;

            .delete-icon {
              fill: var(--color-warning);
            }

            &:disabled {
              border: solid 2px var(--color-inactive);
              cursor: default;

              .delete-icon {
                fill: var(--color-inactive);
              }
            }
          }

          .change-status {
            box-sizing: border-box;
            border-radius: var(--radius-xs);
            padding: 8px;
            width: 120px;
            margin: 0;
            margin-left: 15px;
            background-color: transparent;
            cursor: pointer;
            color: var(--color-text-normal);
            border: 1px solid var(--color-border);
          }
        }

        .wrapper {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          flex-grow: 1;
          margin: 0 10px;
          font-size: 1.1em;

          .button {
            box-sizing: border-box;
            border-radius: var(--radius-xs);
            padding: 8px;
            width: 120px;
            text-align: center;
            cursor: pointer;
            margin: 0;
            position: relative;
            @include hover-overlay;

            &:disabled {
              color: var(--color-inactive);
              background-color: transparent;
              border: solid 1px var(--color-border);
              cursor: default;

              &::after {
                display: none;
              }
            }
          }

          .copy {
            color: var(--color-text-on-fixed-bg);
            background-color: var(--color-bg-dark);
            border: solid 1px var(--color-bg-dark);
            margin-right: 10px;
            font-weight: var(--font-weight-bold);
          }

          .save {
            color: var(--color-text-on-fixed-bg);
            background-color: var(--color-primary);
            border: solid 1px var(--color-primary);
            font-weight: var(--font-weight-bold);
          }
        }
      }

      .mask {
        position: absolute;
        width: 100%;
        height: 100%;
        color: var(--color-text-on-fixed-bg);
        background-color: var(--color-overlay);
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;

        .mask-controller {
          margin-top: 50px;

          .button {
            border: solid 1px var(--color-text-on-fixed-bg);
            border-radius: var(--radius-xs);
            width: 120px;
            cursor: pointer;
          }

          .danger {
            color: var(--color-text-on-fixed-bg);
            background-color: var(--color-warning);
          }
          .cancel {
            background-color: transparent;
            color: var(--color-text-on-fixed-bg);
          }
        }
      }
    }

    .info-belt {
      position: absolute;
      top: 50%;
      left: 0;
      width: 100%;
      height: 35px;
      box-sizing: border-box;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: var(--color-status-completed);
      z-index: -99;
      opacity: 0;
      transition: opacity 0.8s;
      font-weight: var(--font-weight-bold);
      font-size: var(--font-size-xs);
      color: var(--color-text-on-fixed-bg);

      &.visible {
        opacity: 1;
        transition: opacity 0.3s;
        z-index: 9999;
      }
    }
  }
}
</style>
